import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  addPayment,
  addPaymentDetails,
  clearDialog,
  getPaymentMethods,
  LocalizePropType,
  selectBrandSettings,
  selectUser,
  withTextLocalizer,
  isIDVerificationRequired,
  checkoutAddPayment,
} from 'getaway-data-layer';
import { AdyenCheckout, Card, Dropin } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import {
  isDashboardScreen,
  navigateTo,
  showAlert,
  getAdyenCheckoutConfigs,
  parseDropinPaymentMethods,
  reportGAEvent,
  GA_TITLES,
} from '../../../utils';
import CCAdyenComponentView from './CCAdyenComponentView';

type Props = {
  localize: LocalizePropType;
};

const CCAdyenComponent = ({ localize }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const user: any = useSelector(selectUser);
  const brandSettings = useSelector(selectBrandSettings);

  const onClose = () => {
    if (isDashboardScreen()) {
      navigateTo(history, '/settings');
    } else {
      showAlert(
        '',
        localize('cancel.FirstSteps.dialog.message'),
        localize('cancel.FirstSteps.dialog.yes'),
        () => navigateTo(history, '/settings'),
        localize('cancel.FirstSteps.dialog.no'),
        () => {},
        true
      );
    }
  };

  const onSubmitDropinPayment = (state, component, actions) => {
    console.log('onSubmitDropinPayment *****');
    // submit CC
    if (state?.data?.paymentMethod?.holderName) {
      console.log('dispatch(addPayment)');
      console.log('window.location', document.location.href);
      dispatch(
        checkoutAddPayment(
          {
            card: {
              ...state.data.paymentMethod,
              returnUrl: document.location.href,
            },
          },
          (data: any) => {
            console.log('addPayment response: data: ', data);
            if (data.action) {
              component.handleAction(data.action);
            }

            actions.resolve();
            if (isDashboardScreen()) {
              navigateTo(history, '/settings');
            } else {
              if (isIDVerificationRequired(user)) {
                navigateTo(history, `/onboarding/IDOnboarding`);
              } else {
                navigateTo(history, '/onboarding/DLOnboarding');
              }
            }
            reportGAEvent(GA_TITLES.ON_BOARDING.CC_SUBMITTED);
          }
        )
      );
    } else {
      // submit paypal
      dispatch(
        addPayment(
          {
            // @ts-ignore
            paypal: { any: 'any' },
          },
          (user) => {
            setTimeout(() => {
              if (user.paymentMethods[0].action) {
                console.log(user.paymentMethods[0].action);
                component.handleAction(user.paymentMethods[0].action);
                actions.resolve();
              } else {
                actions.reject();
              }
            }, 400);
          }
        )
      );
    }
  };

  const onSubmitDropinPaymentDetails = (state, component, actions) => {
    console.log('onSubmitDropinPaymentDetails *****');
    console.log(state.data);
    console.log('component', component);
    dispatch(
      addPaymentDetails(state.data, (response) => {
        if (response.action) {
          // Drop-in handles the action object from the /payments response
          component.handleAction(response.action);
          actions.resolve();
        } else {
          // Your function to show the final result to the shopper
          if (component) component.setStatus('success');
          dispatch(clearDialog());
          if (isDashboardScreen()) {
            navigateTo(history, '/settings');
          } else {
            if (isIDVerificationRequired(user)) {
              navigateTo(history, `/onboarding/IDOnboarding`);
            } else {
              navigateTo(history, '/onboarding/DLOnboarding');
            }
            // reportGAEvent(GA_TITLES.ON_BOARDING.PAYPAL_SUBMITTED);
          }
          if (actions) actions.resolve();
        }
      })
    );
  };

  const initializeAdyenDropIn = async (paymentMethods: any) => {
    // Create an instance of AdyenCheckout using the configuration object.
    // @ts-ignore
    const checkout = await AdyenCheckout(
      getAdyenCheckoutConfigs(
        user.locale,
        user.address?.country || brandSettings?.defaultCountryCode,
        brandSettings?.adyenClientKey!,
        paymentMethods,
        onSubmitDropinPayment,
        onSubmitDropinPaymentDetails
      )
    );

    // Create the dropin configuration with paymentMethodComponents
    const dropinConfiguration = {
      paymentMethodComponents: [Card],
      paymentMethodsConfiguration: {
        card: {
          hasHolderName: true,
          holderNameRequired: true,
          enableStoreDetails: true,
          hideCVC: false,
          name: 'Credit or debit card',
        },
      },
    };

    // Use the constructor pattern as shown in the documentation
    const dropInComponent = new Dropin(checkout, dropinConfiguration);
    dropInComponent.mount('#card-div');
  };

  const location = useLocation();

  const handleRedirect = async () => {
    // Extract the values from the query string parameters
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get('sessionId');
    const redirectResult = queryParams.get('redirectResult');

    console.log('sessionId', sessionId);
    console.log('redirectResult', redirectResult);
    // If we have both sessionId and redirectResult, handle the redirect
    if (redirectResult) {
      console.log('Handling Adyen redirect with sessionId:', sessionId);

      // Create an instance of AdyenCheckout using the sessionId
      // This follows the Adyen documentation for handling redirects
      dispatch(
        getPaymentMethods(async (paymentMethods) => {
          const checkout = await AdyenCheckout({
            ...getAdyenCheckoutConfigs(
              user.locale,
              user.address?.country || brandSettings?.defaultCountryCode,
              brandSettings?.adyenClientKey!,
              paymentMethods,
              onSubmitDropinPayment,
              onSubmitDropinPaymentDetails
            ),
            onPaymentCompleted: (result, component) => {
              console.log('Payment completed:', result);
              component.setStatus('success');
              dispatch(clearDialog());
              if (isDashboardScreen()) {
                navigateTo(history, '/settings');
              } else {
                if (isIDVerificationRequired(user)) {
                  navigateTo(history, `/onboarding/IDOnboarding`);
                } else {
                  navigateTo(history, '/onboarding/DLOnboarding');
                }
              }
              reportGAEvent(GA_TITLES.ON_BOARDING.CC_SUBMITTED);
            },
            onError: (error, component) => {
              console.error('Payment error:', error);
              if (component) component?.setStatus('error');
            },
          });
          const dropinConfiguration = {
            paymentMethodComponents: [Card],
            paymentMethodsConfiguration: {
              card: {
                hasHolderName: true,
                holderNameRequired: true,
                enableStoreDetails: true,
                hideCVC: false,
                name: 'Credit or debit card',
              },
            },
          };

          const dropInComponent = new Dropin(checkout, dropinConfiguration);
          dropInComponent.mount('#card-div');
          // Submit the redirectResult - this is the key step from the Adyen docs
          checkout.submitDetails({ details: { redirectResult } });
        })
      );
    } else {
      // Normal initialization if not handling a redirect
      dispatch(
        getPaymentMethods((paymentMethods) => {
          initializeAdyenDropIn(parseDropinPaymentMethods(paymentMethods));
        })
      );
    }
  };

  useEffect(() => {
    setTimeout(() => {
      handleRedirect();
    }, 50);
  }, []);

  return (
    <>
      <CCAdyenComponentView localize={localize} onClose={onClose} />
    </>
  );
};

export default withTextLocalizer(CCAdyenComponent);
